import { Reveal } from "@/components/Reveal";
import { SectionHeading, CornerTicks } from "@/components/kit";

const TX_FIELDS = ["WHO", "WHAT", "WHEN", "VALUE"];
const ACTION_FIELDS = ["WHO", "WHAT", "WHEN", "ACTION", "EVIDENCE", "VERIFICATION", "PROOF", "VALUE"];

const TX_FLOW = ["Wallet", "Transaction", "Settlement"];
const ACTION_FLOW = ["Task", "Agent", "Action", "Evidence", "Verification", "Proof", "Settlement"];

const Flow = ({ items, hot = false }) => (
    <div className="flex flex-wrap items-center gap-x-1 gap-y-3">
        {items.map((item, i) => (
            <span key={item} className="flex items-center">
                <span
                    className={`border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] md:text-[11px] ${
                        hot
                            ? "border-[#1677FF]/40 bg-[#1677FF]/[0.08] text-ink"
                            : "border-white/10 bg-[#0A1627]/60 text-mute"
                    }`}
                >
                    {item}
                </span>
                {i < items.length - 1 && (
                    <span className={`mx-1 font-mono text-xs ${hot ? "text-[#1677FF]" : "text-mute/50"}`} aria-hidden="true">
                        →
                    </span>
                )}
            </span>
        ))}
    </div>
);

export const ActionEconomy = () => (
    <section id="action-economy" data-testid="action-economy" className="section relative">
        <div className="absolute inset-0 bg-grid opacity-60" aria-hidden="true" />
        <div className="shell relative">
            <Reveal>
                <SectionHeading
                    index="01"
                    eyebrow="THE ACTION ECONOMY"
                    title={
                        <>
                            THE INTERNET IS MOVING
                            <br />
                            FROM TRANSACTIONS
                            <br />
                            TO <span className="text-[#1677FF]">ACTIONS.</span>
                        </>
                    }
                    description="Blockchains are very good at recording transactions. But transactions do not prove what happened in the real world — or what an autonomous entity actually accomplished."
                />
            </Reveal>

            <div className="mt-14 grid gap-6 lg:grid-cols-2">
                <Reveal delay={0.05}>
                    <div className="relative h-full p-7 md:p-9">
                        <CornerTicks />
                        <div className="flex h-full flex-col border border-white/[0.08] bg-[#0A1627]/50 p-7">
                            <p className="label-tech">TRANSACTION ECONOMY</p>
                            <h3 className="mt-3 font-display text-2xl font-bold uppercase tracking-tightest text-mute">
                                WHAT BLOCKCHAINS RECORD TODAY
                            </h3>
                            <div className="mt-6 flex flex-wrap gap-2">
                                {TX_FIELDS.map((f) => (
                                    <span key={f} className="border border-white/10 px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] text-mute">
                                        {f}
                                    </span>
                                ))}
                            </div>
                            <div className="mt-auto pt-8">
                                <Flow items={TX_FLOW} />
                                <p className="mt-5 text-sm leading-relaxed text-mute/80">
                                    A transaction records that value moved. It cannot tell you whether the work behind it was
                                    really done.
                                </p>
                            </div>
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="relative h-full p-7 md:p-9">
                        <div className="clip-notch h-full border border-[#1677FF]/40 bg-[#0A1627] p-7 shadow-[0_0_60px_rgba(22,119,255,0.12)]">
                            <div className="flex items-center justify-between gap-4">
                                <p className="label-tech text-[#31D8FF]">ACTION ECONOMY</p>
                                <span className="font-mono text-[10px] tracking-[0.2em] text-[#1677FF]">+ 4 FIELDS</span>
                            </div>
                            <h3 className="mt-3 font-display text-2xl font-bold uppercase tracking-tightest text-ink">
                                WHAT ACTRA INTRODUCES
                            </h3>
                            <div className="mt-6 flex flex-wrap gap-2">
                                {ACTION_FIELDS.map((f, i) => (
                                    <span
                                        key={f}
                                        className={`border px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] ${
                                            i > 2 ? "border-[#1677FF]/50 bg-[#1677FF]/10 text-[#8CB8FF]" : "border-white/10 text-ink"
                                        }`}
                                    >
                                        {f}
                                    </span>
                                ))}
                            </div>
                            <div className="pt-8">
                                <Flow items={ACTION_FLOW} hot />
                                <p className="mt-5 text-sm leading-relaxed text-mute">
                                    ACTRA transforms actions into verifiable economic events — with evidence, verification and
                                    proof attached to every outcome.
                                </p>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
);

export default ActionEconomy;
